import { Fragment, useMemo, useState } from "react"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import { DateTime } from "luxon"
import If from "../../../if"
import ProgressBar from "../../../progress-bar"
import { FourColumnPerformanceSkeleton } from "../../../skeletons"
import { GraphSkeleton } from "../../../skeletons"
import NoData from "../../../no-data"
import { getAreaSplineChartOptions } from "../../../chart-with-stats/options"
import { useFetchSubscriptionEventCountByBrandQuery } from "../../../../../redux/queries"

interface SubscriptionEventBrandsProps {
  title: string
  range: number
  orgId: string | undefined
  loid: string | undefined
  mode?: "brands" | "timeseries"
}

const EVENT_TYPE_OPTIONS = [
  { value: "dashboard_pageview", label: "Dashboard views" },
  { value: "dashboard_ai_created", label: "AI Advisor sessions" },
  { value: "dashboard_first_visit", label: "First visits" },
  { value: "dashboard_contact", label: "Dashboard contacts" },
  { value: "dashboard_update", label: "Dashboard updates" },
  { value: "reactivated", label: "Reactivations" },
  { value: "created_from_pending", label: "Dream rate conversions" }
]

const MAX_BRANDS = 15
const dateFormat = "yyyy-MM-dd"
const labelDateFormat = "LLL dd yy"

const SubscriptionEventBrands = ({
  title,
  range,
  orgId,
  loid,
  mode = "brands"
}: SubscriptionEventBrandsProps) => {
  const [eventType, setEventType] = useState("dashboard_pageview")

  const { data, isFetching } = useFetchSubscriptionEventCountByBrandQuery({
    range,
    period: 1,
    event_name: eventType,
    org_id: orgId,
    loid
  })

  const brands = data?.brands?.slice(0, MAX_BRANDS) ?? []
  const maxCount = brands.length > 0 ? brands[0].total : 0

  const chartLabels = useMemo(() => {
    if (mode !== "timeseries" || !data?.dateSeries) return []
    return data.dateSeries
      .map(item => ({
        date: DateTime.fromFormat(item.date, dateFormat),
        y: item.count,
        color: "#786ff6"
      }))
      .sort((a, b) => (a.date > b.date ? 1 : -1))
      .map(item => ({
        ...item,
        name: item.date.toFormat(labelDateFormat)
      }))
  }, [data?.dateSeries, mode])

  const containsData = chartLabels.length > 0

  const eventTypeDropdown = (
    <select
      value={eventType}
      onChange={e => setEventType(e.target.value)}
      className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-400"
    >
      {EVENT_TYPE_OPTIONS.map(opt => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  )

  if (mode === "timeseries") {
    return (
      <div className="bg-white flex flex-col flex-1 px-6 pt-6 pb-4 rounded-md overflow-hidden">
        <div className="flex gap-4 lg:gap-0 content-center items-center justify-between flex-wrap">
          <h3 className="text-base font-medium">{title}</h3>
          {eventTypeDropdown}
        </div>
        <If
          condition={isFetching}
          then={<GraphSkeleton className="mt-1 pt-12" />}
          else={
            <Fragment>
              <div className="pt-11 pb-5 relative">
                {!containsData && (
                  <div className="absolute top-24 left-1/2 text-sm z-[1]">
                    No data yet
                  </div>
                )}
                <HighchartsReact
                  highcharts={Highcharts}
                  options={getAreaSplineChartOptions(
                    containsData,
                    chartLabels,
                    "events",
                    "",
                    {
                      fillOpacity: 0,
                      lineWidth: 2,
                      lineColor: "#786ff6"
                    }
                  )}
                  containerProps={{ style: { height: "225px" } }}
                />
              </div>
              <div className="flex text-xs">
                <div className="flex flex-col">
                  <div className="leading-5">TOTAL EVENTS</div>
                  <div className="text-lg leading-6 font-inter-extrabold">
                    {data?.grandTotal?.toLocaleString() ?? "-"}
                  </div>
                </div>
              </div>
            </Fragment>
          }
        />
      </div>
    )
  }

  return (
    <div className="bg-white flex flex-col flex-1 px-6 pt-6 pb-7 rounded-md overflow-hidden">
      <div className="flex gap-4 lg:gap-0 content-center items-center justify-between flex-wrap">
        <h3 className="text-base font-medium">{title}</h3>
        {eventTypeDropdown}
      </div>

      <If
        condition={isFetching}
        then={<FourColumnPerformanceSkeleton className="mt-px pt-11" />}
        else={
          <If
            condition={!brands.length}
            then={<NoData />}
            else={
              <>
                <p className="text-sm text-gray-500 mt-3">
                  {data?.grandTotal?.toLocaleString()} total sessions &middot;{" "}
                  {data?.brandCount} brands
                </p>
                <div
                  className="mt-4 overflow-y-auto scroll-smooth"
                  style={{ height: "330px" }}
                >
                  <div className="w-full grid gap-y-6 py-2">
                    {brands.map((brand, index) => (
                      <ProgressBar
                        key={index}
                        count={brand.total}
                        maxCount={maxCount}
                        label={brand.name}
                      />
                    ))}
                  </div>
                </div>
              </>
            }
          />
        }
      />
    </div>
  )
}

export default SubscriptionEventBrands
